import { useCallback, useEffect, useState } from 'react';

import type {
  VerificationProfileList,
  VerificationProfileReviewerOption,
  VerificationProfileSummary,
} from '@nightshift/ui-types';

import { Empty, Kicker, Spinner } from '../components/primitives.tsx';
import { useSettingsAutosave } from '../lib/settings-autosave.tsx';
import { comparisonOptions } from '../lib/performance-targets.ts';
import {
  canWriteProfiles,
  contractWriteBody,
  draftFromSummary,
  PROFILE_CAPTURE_SUGGESTIONS,
  PROFILE_THRESHOLD_METRICS,
  toggleNamed,
} from '../lib/profiles.ts';
import { api, type SessionUser } from '../live/api.ts';

type Draft = ReturnType<typeof draftFromSummary>;

export function ProfilesPanel({ user }: { user: SessionUser | null }) {
  const canWrite = canWriteProfiles(user);
  const [page, setPage] = useState<VerificationProfileList | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPage(await api.verificationProfiles());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !page) {
    return (
      <Empty
        title="Ticket types could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!page) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s8)' }}>
        <Spinner size={20} />
      </div>
    );
  }

  return (
    <div className="profiles-settings-panel">
      <p className="sheet-note cfg-provenance">
        {page.cascadeNote} Coded default: <code className="mono">{page.codedDefault}</code>. Saving
        inserts a new contract version; older versions stay on tickets that already pinned them.
        Project Settings → Performance can overlay extra metrics on top of a profile threshold.
        {canWrite ? null : ' Only an admin can change ticket types.'}
      </p>
      {page.items.length === 0 ? (
        <Empty
          title="No ticket types in this database."
          body="Migrations seed four MVP names. If this list is empty, the catalog was wiped after migrate and not re-seeded."
        />
      ) : null}
      {page.items.map((row) => (
        <ProfileEditor
          key={row.id}
          row={row}
          reviewers={page.availableReviewers}
          canWrite={canWrite}
          onSaved={load}
        />
      ))}
    </div>
  );
}

function ProfileEditor({
  row,
  reviewers,
  canWrite,
  onSaved,
}: {
  row: VerificationProfileSummary;
  reviewers: VerificationProfileReviewerOption[];
  canWrite: boolean;
  onSaved: (message: string) => Promise<void>;
}) {
  const [captureExtra, setCaptureExtra] = useState('');
  const persist = useCallback(async (draft: Draft) => {
    await api.updateVerificationProfile(row.name, contractWriteBody(draft));
    await onSaved('saved');
  }, [onSaved, row.name]);
  const autosave = useSettingsAutosave({
    value: draftFromSummary(row),
    save: persist,
    what: `the ticket type ${row.name}`,
    enabled: canWrite,
    delay: 900,
  });
  const { draft, setDraft, status, error } = autosave;

  const namedReviewers = new Set(reviewers.map((entry) => entry.name));
  const orphanReviewers = draft.reviewerNames.filter((name) => !namedReviewers.has(name));
  const extraCaptures = draft.captureNames.filter(
    (name) => !PROFILE_CAPTURE_SUGGESTIONS.includes(name as (typeof PROFILE_CAPTURE_SUGGESTIONS)[number]),
  );

  return (
    <section className="sheet-panel" data-profile={row.name}>
      <header className="panel-head">
        <Kicker>{row.name}</Kicker>
        <span className="mono dim" style={{ marginLeft: 'auto', fontSize: 'var(--fs-micro)' }}>
          {draft.ci}
          {row.contractVersion != null ? ` · v${row.contractVersion}` : ''}
        </span>
      </header>
      <div className="sheet-panel-body">
        <div className="reviewers-fields">
          <div className="cfg-field">
            <div style={{ minWidth: 0 }}>
              <span className="cfg-label">CI</span>
              <p className="cfg-help">
                Required parks when the repo has no pipeline. Skip still enters GitLab CI, then
                passes immediately.
              </p>
            </div>
            <div className="cfg-control">
              <select
                className="input"
                value={draft.ci}
                disabled={!canWrite}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    ci: event.target.value === 'skip' ? 'skip' : 'required',
                  }))
                }
              >
                <option value="required">required</option>
                <option value="skip">skip</option>
              </select>
            </div>
          </div>

          <FlagRow
            label="Build"
            help="GitLab must produce BuildEvidence on the MR head."
            value={draft.buildRequired}
            canWrite={canWrite}
            onChange={(value) => setDraft((current) => ({ ...current, buildRequired: value }))}
          />
          <FlagRow
            label="Unit tests"
            help="GitLab must produce TestEvidence on the MR head."
            value={draft.unitTestsRequired}
            canWrite={canWrite}
            onChange={(value) => setDraft((current) => ({ ...current, unitTestsRequired: value }))}
          />
          <FlagRow
            label="Human review"
            help="Merge still needs a human. Turning this off is almost never what you want."
            value={draft.humanReviewRequired}
            canWrite={canWrite}
            onChange={(value) => setDraft((current) => ({ ...current, humanReviewRequired: value }))}
          />

          <div className="reviewers-field">
            <span className="cfg-label">Reviewers</span>
            <p className="cfg-help">
              Named reviewers run in parallel at AI Review. Add the agent on the Reviewers tab
              first, then tick it here.
            </p>
            <div className="profiles-checks">
              {reviewers.map((entry) => (
                <label key={entry.name} className="profiles-check">
                  <input
                    type="checkbox"
                    checked={draft.reviewerNames.includes(entry.name)}
                    disabled={!canWrite}
                    onChange={() =>
                      setDraft((current) => ({
                        ...current,
                        reviewerNames: toggleNamed(current.reviewerNames, entry.name),
                      }))
                    }
                  />
                  <span>
                    {entry.name}
                    {entry.enabled ? null : <span className="dim"> · disabled</span>}
                  </span>
                </label>
              ))}
              {orphanReviewers.map((name) => (
                <label key={name} className="profiles-check">
                  <input
                    type="checkbox"
                    checked
                    disabled={!canWrite}
                    onChange={() =>
                      setDraft((current) => ({
                        ...current,
                        reviewerNames: toggleNamed(current.reviewerNames, name),
                      }))
                    }
                  />
                  <span>
                    {name}
                    <span className="dim"> · missing reviewer row</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="reviewers-field">
            <span className="cfg-label">Capture artefacts</span>
            <p className="cfg-help">
              Tokens the CI job name must contain so Nightshift can attach CaptureEvidence.
              visual-change needs capture; gameplay-change needs capture and launch.
            </p>
            <div className="profiles-checks">
              {PROFILE_CAPTURE_SUGGESTIONS.map((name) => (
                <label key={name} className="profiles-check">
                  <input
                    type="checkbox"
                    checked={draft.captureNames.includes(name)}
                    disabled={!canWrite}
                    onChange={() =>
                      setDraft((current) => ({
                        ...current,
                        captureNames: toggleNamed(current.captureNames, name),
                      }))
                    }
                  />
                  <span>{name}</span>
                </label>
              ))}
              {extraCaptures.map((name) => (
                <label key={name} className="profiles-check">
                  <input
                    type="checkbox"
                    checked
                    disabled={!canWrite}
                    onChange={() =>
                      setDraft((current) => ({
                        ...current,
                        captureNames: toggleNamed(current.captureNames, name),
                      }))
                    }
                  />
                  <span>{name}</span>
                </label>
              ))}
            </div>
            {canWrite ? (
              <div className="profiles-add-row">
                <input
                  className="input"
                  value={captureExtra}
                  placeholder="Extra token"
                  onChange={(event) => setCaptureExtra(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    const name = captureExtra.trim();
                    if (!name) return;
                    setDraft((current) => ({
                      ...current,
                      captureNames: current.captureNames.includes(name)
                        ? current.captureNames
                        : [...current.captureNames, name],
                    }));
                    setCaptureExtra('');
                  }}
                />
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => {
                    const name = captureExtra.trim();
                    if (!name) return;
                    setDraft((current) => ({
                      ...current,
                      captureNames: current.captureNames.includes(name)
                        ? current.captureNames
                        : [...current.captureNames, name],
                    }));
                    setCaptureExtra('');
                  }}
                >
                  Add
                </button>
              </div>
            ) : null}
          </div>

          <div className="reviewers-field">
            <span className="cfg-label">Performance threshold</span>
            <p className="cfg-help">
              Profile default. Settings → Performance overlays extra per-project targets and can
              replace this metric.
            </p>
            <label className="profiles-check">
              <input
                type="checkbox"
                checked={draft.performanceThreshold !== null}
                disabled={!canWrite}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    performanceThreshold: event.target.checked
                      ? current.performanceThreshold ?? { metric: 'fps', comparison: 'gte', value: 60 }
                      : null,
                  }))
                }
              />
              <span>Gate on a performance sample</span>
            </label>
            {draft.performanceThreshold ? (
              <div className="profiles-threshold">
                <select
                  className="input"
                  value={draft.performanceThreshold.metric}
                  disabled={!canWrite}
                  aria-label="Threshold metric"
                  onChange={(event) =>
                    setDraft((current) =>
                      current.performanceThreshold
                        ? {
                            ...current,
                            performanceThreshold: {
                              ...current.performanceThreshold,
                              metric: event.target.value,
                            },
                          }
                        : current,
                    )
                  }
                >
                  {PROFILE_THRESHOLD_METRICS.map((metric) => (
                    <option key={metric} value={metric}>
                      {metric}
                    </option>
                  ))}
                  {PROFILE_THRESHOLD_METRICS.includes(
                    draft.performanceThreshold.metric as (typeof PROFILE_THRESHOLD_METRICS)[number],
                  ) ? null : (
                    <option value={draft.performanceThreshold.metric}>
                      {draft.performanceThreshold.metric}
                    </option>
                  )}
                </select>
                <select
                  className="input"
                  value={draft.performanceThreshold.comparison}
                  disabled={!canWrite}
                  aria-label="Threshold comparison"
                  onChange={(event) =>
                    setDraft((current) =>
                      current.performanceThreshold
                        ? {
                            ...current,
                            performanceThreshold: {
                              ...current.performanceThreshold,
                              comparison: event.target.value,
                            },
                          }
                        : current,
                    )
                  }
                >
                  {comparisonOptions().map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  className="input"
                  type="number"
                  value={draft.performanceThreshold.value}
                  disabled={!canWrite}
                  aria-label="Threshold value"
                  onChange={(event) =>
                    setDraft((current) =>
                      current.performanceThreshold
                        ? {
                            ...current,
                            performanceThreshold: {
                              ...current.performanceThreshold,
                              value: Number(event.target.value),
                            },
                          }
                        : current,
                    )
                  }
                />
              </div>
            ) : null}
          </div>
        </div>
        {status === 'saving' ? <p className="cfg-help">Publishing a new version…</p> : null}
        {error ? <p className="cfg-error" role="alert">Unsaved: {error}</p> : null}
      </div>
    </section>
  );
}

function FlagRow({
  label,
  help,
  value,
  canWrite,
  onChange,
}: {
  label: string;
  help: string;
  value: boolean;
  canWrite: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="cfg-field">
      <div style={{ minWidth: 0 }}>
        <span className="cfg-label">{label}</span>
        <p className="cfg-help">{help}</p>
      </div>
      <div className="cfg-control">
        <button
          type="button"
          className="btn btn-sm"
          disabled={!canWrite}
          aria-pressed={value}
          onClick={() => onChange(!value)}
        >
          {value ? 'Required' : 'Not required'}
        </button>
      </div>
    </div>
  );
}
